import { useAuth0 } from "@auth0/auth0-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { FeedGroupBy } from "@/features/feed/feedDisplayGroups";
import type { HomeMetricsPeriod } from "@/features/dashboard/home/homeDateRangePresets";
import type { HomeMetricsServiceTrade } from "@/features/dashboard/home/homeServiceTradePresets";

import {
  DEFAULT_USER_PREFERENCES,
  loadUserPreferencesForSub,
  saveUserPreferencesForSub,
  type UserPreferences,
} from "./userPreferencesStorage";

export type { UserPreferences };

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  setShowLocodes: (value: boolean) => void;
  setShowCarrierLogos: (value: boolean) => void;
  setShowWeekNumbers: (value: boolean) => void;
  setHideSensitiveData: (value: boolean) => void;
  setScopeToMyBookings: (value: boolean) => void;
  setFeedGroupBy: (value: FeedGroupBy) => void;
  hideFeedGroup: (sectionKey: string) => void;
  restoreFeedGroup: (sectionKey: string) => void;
  clearFeedHiddenGroups: () => void;
  setNotificationSounds: (value: boolean) => void;
  setHomeMetricsPeriod: (value: HomeMetricsPeriod) => void;
  setHomeMetricsServiceTrade: (value: HomeMetricsServiceTrade) => void;
  updatePreferences: (patch: Partial<UserPreferences>) => void;
}

/** sessionStorage keys set when the user clears the default "my bookings" filter. */
export const MY_BOOKINGS_SCOPE_DISMISS_KEYS = [
  "bookings-my-scope-dismissed",
] as const;

export function clearMyBookingsScopeDismissals() {
  try {
    for (const key of MY_BOOKINGS_SCOPE_DISMISS_KEYS) {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore quota / private-mode */
  }
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(
  null,
);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const userSub = user?.sub ?? null;
  const [preferences, setPreferences] = useState<UserPreferences>(
    DEFAULT_USER_PREFERENCES,
  );
  const userSubRef = useRef<string | null>(null);
  userSubRef.current = userSub;

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated || !userSub) {
      userSubRef.current = null;
      setPreferences(DEFAULT_USER_PREFERENCES);
      clearMyBookingsScopeDismissals();
      return;
    }

    userSubRef.current = userSub;
    setPreferences(loadUserPreferencesForSub(userSub));
  }, [isAuthenticated, isLoading, userSub]);

  const persist = useCallback((prefs: UserPreferences) => {
    const sub = userSubRef.current;
    if (!sub) return;
    saveUserPreferencesForSub(sub, prefs);
  }, []);

  const updatePreferences = useCallback(
    (patch: Partial<UserPreferences>) => {
      setPreferences((prev) => {
        const next = { ...prev, ...patch };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const setShowLocodes = useCallback(
    (value: boolean) => updatePreferences({ showLocodes: value }),
    [updatePreferences],
  );

  const setShowCarrierLogos = useCallback(
    (value: boolean) => updatePreferences({ showCarrierLogos: value }),
    [updatePreferences],
  );

  const setShowWeekNumbers = useCallback(
    (value: boolean) => updatePreferences({ showWeekNumbers: value }),
    [updatePreferences],
  );

  const setHideSensitiveData = useCallback(
    (value: boolean) => updatePreferences({ hideSensitiveData: value }),
    [updatePreferences],
  );

  const setScopeToMyBookings = useCallback(
    (value: boolean) => {
      if (value) clearMyBookingsScopeDismissals();
      updatePreferences({ scopeToMyBookings: value });
    },
    [updatePreferences],
  );

  const setFeedGroupBy = useCallback(
    (value: FeedGroupBy) => updatePreferences({ feedGroupBy: value }),
    [updatePreferences],
  );

  const hideFeedGroup = useCallback(
    (sectionKey: string) => {
      setPreferences((prev) => {
        if (prev.feedHiddenGroups.includes(sectionKey)) return prev;
        const next = {
          ...prev,
          feedHiddenGroups: [...prev.feedHiddenGroups, sectionKey],
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const restoreFeedGroup = useCallback(
    (sectionKey: string) => {
      setPreferences((prev) => {
        const feedHiddenGroups = prev.feedHiddenGroups.filter(
          (key) => key !== sectionKey,
        );
        if (feedHiddenGroups.length === prev.feedHiddenGroups.length) return prev;
        const next = { ...prev, feedHiddenGroups };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const clearFeedHiddenGroups = useCallback(() => {
    updatePreferences({ feedHiddenGroups: [] });
  }, [updatePreferences]);

  const setNotificationSounds = useCallback(
    (value: boolean) => updatePreferences({ notificationSounds: value }),
    [updatePreferences],
  );

  const setHomeMetricsPeriod = useCallback(
    (value: HomeMetricsPeriod) => updatePreferences({ homeMetricsPeriod: value }),
    [updatePreferences],
  );

  const setHomeMetricsServiceTrade = useCallback(
    (value: HomeMetricsServiceTrade) =>
      updatePreferences({ homeMetricsServiceTrade: value }),
    [updatePreferences],
  );

  const value = useMemo(
    () => ({
      preferences,
      setShowLocodes,
      setShowCarrierLogos,
      setShowWeekNumbers,
      setHideSensitiveData,
      setScopeToMyBookings,
      setFeedGroupBy,
      hideFeedGroup,
      restoreFeedGroup,
      clearFeedHiddenGroups,
      setNotificationSounds,
      setHomeMetricsPeriod,
      setHomeMetricsServiceTrade,
      updatePreferences,
    }),
    [
      preferences,
      setShowLocodes,
      setShowCarrierLogos,
      setShowWeekNumbers,
      setHideSensitiveData,
      setScopeToMyBookings,
      setFeedGroupBy,
      hideFeedGroup,
      restoreFeedGroup,
      clearFeedHiddenGroups,
      setNotificationSounds,
      setHomeMetricsPeriod,
      setHomeMetricsServiceTrade,
      updatePreferences,
    ],
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  }
  return ctx;
}
